import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { TrendingUp, DollarSign, Users, AlertCircle, FileText, CheckCircle, XCircle, Filter, Download, Clock } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Couleurs pour les graphiques
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function StatistiquesAdmin({ getAuthHeader }) {
  const [loading, setLoading] = useState(true);
  const [statistiques, setStatistiques] = useState(null);
  const [dispositifs, setDispositifs] = useState([]);
  const [_membres, setMembres] = useState([]);
  const [coopers, setCoopers] = useState([]);
  
  // Filtres
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [dispositifFiltre, setDispositifFiltre] = useState('all');
  const [membreFiltre, setMembreFiltre] = useState('all');
  const [cooperFiltre, setCooperFiltre] = useState('all');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Récupérer les listes pour les filtres
      const [dispositifsRes, membresRes, coopersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dispositifs`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/members`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);

      // Trier du plus récent au plus ancien
      const sortByDate = (arr) => (arr || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setDispositifs(sortByDate(dispositifsRes.data || []));
      setMembres(sortByDate(membresRes.data || []));
      setCoopers(sortByDate(coopersRes.data?.coopers || coopersRes.data || []));

      // Charger les statistiques
      await fetchStatistiques();
    } catch {
    }
  };

  const fetchStatistiques = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      if (dispositifFiltre && dispositifFiltre !== 'all') params.dispositif_id = dispositifFiltre;
      if (membreFiltre && membreFiltre !== 'all') params.membre_id = membreFiltre;
      if (cooperFiltre && cooperFiltre !== 'all') params.cooper_id = cooperFiltre;

      const response = await axios.get(`${BACKEND_URL}/api/admin/statistiques`, {
        headers: getAuthHeader(),
        params
      });

      setStatistiques(response.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setDateDebut('');
    setDateFin('');
    setDispositifFiltre('all');
    setMembreFiltre('all');
    setCooperFiltre('all');
    setTimeout(() => fetchStatistiques(), 100);
  };

  const exportStats = () => {
    if (!statistiques) return;
    
    const dataStr = JSON.stringify(statistiques, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `statistiques_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  if (loading && !statistiques) {
    return (
      <div className="flex items-center justify-center p-4 md:p-8">
        <div className="text-lg text-gray-600">Chargement des statistiques...</div>
      </div>
    );
  }

  if (!statistiques) {
    return (
      <div className="flex items-center justify-center p-4 md:p-8">
        <div className="text-lg text-red-600">Erreur lors du chargement des statistiques</div>
      </div>
    );
  }

  // Préparer les données pour le camembert (Répartition par dispositif)
  const pieDataDispositifs = statistiques.repartition_dispositifs.slice(0, 5).map((item, _index) => ({
    name: item.nom,
    value: item.montant,
    nombre: item.nombre_souscriptions
  }));

  // Préparer les données pour le diagramme en barres (COOPER'S)
  const barDataCoopers = statistiques.repartition_coopers.slice(0, 10).map(item => ({
    name: item.nom.length > 20 ? item.nom.substring(0, 20) + '...' : item.nom,
    montant: item.montant,
    membres: item.nombre_membres
  }));

  return (
    <div className="space-y-6">
      {/* En-tête avec filtres */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Statistiques Globales
              </CardTitle>
              <CardDescription>
                Visualisez les performances de la plateforme avec filtres avancés
              </CardDescription>
            </div>
            <Button onClick={exportStats} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Filtres de dates */}
            <div>
              <Label>Date début</Label>
              <DatePickerFr
                className="w-full mt-1"
                value={dateDebut}
                onChange={setDateDebut}
              />
            </div>
            <div>
              <Label>Date fin</Label>
              <DatePickerFr
                className="w-full mt-1"
                value={dateFin}
                onChange={setDateFin}
              />
            </div>

            {/* Filtre par dispositif */}
            <div>
              <Label>Dispositif</Label>
              <Select value={dispositifFiltre} onValueChange={setDispositifFiltre}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {dispositifs.filter(d => d.id && d.nom).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtre par COOPER'S */}
            <div>
              <Label>COOPER'S</Label>
              <Select value={cooperFiltre} onValueChange={setCooperFiltre}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {coopers.filter(c => c.id && c.entreprise).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.entreprise}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-2 items-end">
              <Button onClick={fetchStatistiques} className="flex-1">
                <Filter className="h-4 w-4 mr-2" />
                Filtrer
              </Button>
              <Button onClick={handleResetFilters} variant="outline">
                Réinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Montant Total (Montant de base)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {statistiques.montant_total_ht.toLocaleString('fr-FR')}€
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Taxes: {statistiques.taxes_totales.toLocaleString('fr-FR')}€
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              Membres & COOPER'S
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {statistiques.nombre_membres}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {statistiques.nombre_coopers} COOPER'S actifs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-purple-600" />
              Souscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {statistiques.nombre_souscriptions}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {statistiques.nombre_souscriptions_actives} actives
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              Dommages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {statistiques.nombre_dommages}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {statistiques.nombre_dommages_en_cours} en cours
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camembert - Répartition par dispositif */}
        <Card>
          <CardHeader>
            <CardTitle>Répartition par Dispositif</CardTitle>
            <CardDescription>
              Top 5 des dispositifs par montant encaissé
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieDataDispositifs}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieDataDispositifs.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `${value.toLocaleString('fr-FR')}€`}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {pieDataDispositifs.map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span>{item.name}</span>
                  </div>
                  <div className="font-semibold">
                    {item.value.toLocaleString('fr-FR')}€
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Diagramme en barres - COOPER'S */}
        <Card>
          <CardHeader>
            <CardTitle>Performance des COOPER'S</CardTitle>
            <CardDescription>
              Top 10 des COOPER'S par chiffre d'affaires
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barDataCoopers}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={100}
                  fontSize={11}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'montant') return [`${value.toLocaleString('fr-FR')}€`, 'Montant'];
                    return [value, 'Membres'];
                  }}
                />
                <Legend />
                <Bar dataKey="montant" fill="#3b82f6" name="Montant (€)" />
                <Bar dataKey="membres" fill="#10b981" name="Nombre de membres" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Évolution mensuelle */}
      <Card>
        <CardHeader>
          <CardTitle>Évolution Mensuelle</CardTitle>
          <CardDescription>
            Montant des souscriptions sur les 12 derniers mois
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={statistiques.evolution_mensuelle}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mois" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'montant') return [`${value.toLocaleString('fr-FR')}€`, 'Montant'];
                  return [value, 'Souscriptions'];
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="montant" 
                stroke="#3b82f6" 
                strokeWidth={2}
                name="Montant (€)"
              />
              <Line 
                type="monotone" 
                dataKey="nombre_souscriptions" 
                stroke="#10b981" 
                strokeWidth={2}
                name="Nombre de souscriptions"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Détails dommages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Statistiques Dommages</CardTitle>
            <CardDescription>Répartition par statut</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(statistiques.dommages_par_statut).map(([statut, nombre]) => {
                const labels = {
                  en_attente: { text: 'En attente', icon: Clock, color: 'text-orange-600' },
                  en_cours: { text: 'En cours', icon: TrendingUp, color: 'text-blue-600' },
                  evalue: { text: 'Évalué', icon: CheckCircle, color: 'text-green-600' },
                  cloture: { text: 'Clôturé', icon: XCircle, color: 'text-gray-600' }
                };
                const label = labels[statut] || { text: statut, icon: FileText, color: 'text-gray-600' };
                const Icon = label.icon;
                
                return (
                  <div key={statut} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${label.color}`} />
                      <span className="font-medium">{label.text}</span>
                    </div>
                    <div className="text-2xl font-bold">{nombre}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Évaluation Financière des Dommages</CardTitle>
            <CardDescription>Montant total évalué</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-5xl font-bold text-red-600 mb-4">
                {statistiques.evaluation_dommages_totale.toLocaleString('fr-FR')}€
              </div>
              <p className="text-gray-600 text-center">
                Montant total des dommages évalués et clôturés
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 w-full">
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {statistiques.nombre_dommages_en_cours}
                  </div>
                  <div className="text-sm text-gray-600">En cours</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">
                    {statistiques.nombre_dommages_clotures}
                  </div>
                  <div className="text-sm text-gray-600">Clôturés</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
